(() => {
  const badgeSelector = '#cart-icon-bubble';
  let syncRequest;
  let syncQueued = false;

  const updateBadge = (count) => {
    document.querySelectorAll(badgeSelector).forEach((badge) => {
      const bubble = badge.querySelector('.cart-count-bubble');

      if (count <= 0) {
        bubble?.remove();
        return;
      }

      if (!bubble) return;

      const visibleCount = bubble.querySelector('[aria-hidden="true"]');
      if (visibleCount) {
        visibleCount.textContent = count < 100 ? String(count) : '';
      }
    });
  };

  const getVisibleCartCount = () => {
    return [...document.querySelectorAll('cart-items .quantity__input')].reduce((count, input) => {
      if (input.dataset.cartCounted === 'false') return count;

      const quantity = Number.parseInt(input.value, 10);
      return count + (Number.isFinite(quantity) ? quantity : 0);
    }, 0);
  };

  const updateFromCartInputs = () => {
    const inputs = document.querySelectorAll('cart-items .quantity__input');
    if (inputs.length > 0) updateBadge(getVisibleCartCount());
  };

  const getCartBadgeContent = (html) => {
    if (!html) return '';

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const section = parsed.querySelector('#shopify-section-cart-icon-bubble');
    if (section) return section.innerHTML;

    const nextBadge = parsed.querySelector(badgeSelector);
    return nextBadge ? nextBadge.innerHTML : parsed.body.innerHTML;
  };

  const replaceCartDrawer = (html) => {
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextDrawer = parsed.querySelector('cart-drawer');
    const currentDrawer = document.querySelector('cart-drawer');
    if (!nextDrawer || !currentDrawer) return;

    const wasOpen = currentDrawer.classList.contains('active');
    currentDrawer.innerHTML = nextDrawer.innerHTML;
    currentDrawer.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));
    currentDrawer.classList.toggle('active', wasOpen);

    const timer = currentDrawer.querySelector('countdown-timer');
    if (timer && typeof timer.playTimer === 'function') timer.playTimer();
  };

  const replaceCartDrawerSummary = (html) => {
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextDrawer = parsed.querySelector('cart-drawer');
    const currentDrawer = document.querySelector('cart-drawer');
    if (!nextDrawer || !currentDrawer) return;

    const nextIsEmpty = nextDrawer.classList.contains('is-empty');
    const currentIsEmpty = currentDrawer.classList.contains('is-empty');
    if (nextIsEmpty || currentIsEmpty) {
      replaceCartDrawer(html);
      return;
    }

    ['.drawer__header', '#CartDrawer > .drawer__inner > .drawer__footer'].forEach((selector) => {
      const currentContent = currentDrawer.querySelector(selector);
      const nextContent = nextDrawer.querySelector(selector);
      if (currentContent && nextContent) currentContent.replaceWith(nextContent);
    });
  };

  const replaceCartBadge = (html) => {
    const content = getCartBadgeContent(html);
    if (!content) return;

    document.querySelectorAll(badgeSelector).forEach((badge) => {
      badge.innerHTML = content;
    });
  };

  const replaceSectionContents = (html, containerSelector, contentSelector = '.js-contents') => {
    if (!html) return;

    const currentContainer = document.querySelector(containerSelector);
    if (!currentContainer) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextContainer = parsed.querySelector(containerSelector);
    if (!nextContainer) return;

    const currentContent = currentContainer.querySelector(contentSelector);
    const nextContent = nextContainer.querySelector(contentSelector);
    if (currentContent && nextContent) currentContent.innerHTML = nextContent.innerHTML;

    currentContainer.classList.toggle('is-empty', nextContainer.classList.contains('is-empty'));

    const currentCartItems = currentContainer.closest('cart-items');
    const nextCartItems = nextContainer.closest('cart-items');
    if (currentCartItems && nextCartItems) {
      currentCartItems.classList.toggle('is-empty', nextCartItems.classList.contains('is-empty'));
    }
  };

  const replaceCartLiveRegion = (html) => {
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextRegion = parsed.querySelector('#cart-live-region-text');
    const currentRegion = document.querySelector('#cart-live-region-text');
    if (nextRegion && currentRegion) currentRegion.innerHTML = nextRegion.innerHTML;
  };

  const getSectionsToSync = () => {
    const mainItemsSection = document.querySelector('#main-cart-items')?.dataset.id;
    const mainFooterSection = document.querySelector('#main-cart-footer')?.dataset.id;

    return [
      mainItemsSection,
      mainFooterSection,
      'cart-live-region-text',
      'cart-drawer',
      'cart-icon-bubble',
    ].filter((section, index, sections) => section && sections.indexOf(section) === index);
  };

  const renderSyncedSections = (sections) => {
    const mainItemsSection = document.querySelector('#main-cart-items')?.dataset.id;
    const mainFooterSection = document.querySelector('#main-cart-footer')?.dataset.id;

    if (mainItemsSection) {
      replaceSectionContents(sections[mainItemsSection], '#main-cart-items');
    }
    if (mainFooterSection) {
      replaceSectionContents(sections[mainFooterSection], '#main-cart-footer');
    }

    replaceCartLiveRegion(sections['cart-live-region-text']);
    replaceCartDrawer(sections['cart-drawer']);
    replaceCartBadge(sections['cart-icon-bubble']);
  };

  const renderCartSections = (cart) => {
    const drawer = document.querySelector('cart-drawer');

    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents(cart, true);
      return;
    }

    replaceCartDrawer(cart.sections?.['cart-drawer']);
    replaceCartBadge(cart.sections?.['cart-icon-bubble']);
  };

  const removeDrawerItem = async (button) => {
    if (button.dataset.removing === 'true') return;

    const line = Number.parseInt(button.dataset.index, 10);
    if (!Number.isFinite(line) || line < 1) return;

    button.dataset.removing = 'true';
    const row = button.closest('.cart-item');
    row?.classList.add('cart-item--updating');

    try {
      const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          line,
          quantity: 0,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error(`Cart remove request failed: ${response.status}`);

      const cart = await response.json();
      if (!cart.sections?.['cart-drawer']) {
        const sectionsResponse = await fetch(
          `${window.routes?.cart_url || '/cart'}?sections=cart-drawer,cart-icon-bubble`,
          { credentials: 'same-origin', cache: 'no-store' },
        );
        if (!sectionsResponse.ok) throw new Error(`Cart sections request failed: ${sectionsResponse.status}`);
        cart.sections = await sectionsResponse.json();
      }

      renderCartSections(cart);
      document.dispatchEvent(new CustomEvent('cartQuantityUpdated', { detail: { cart } }));
    } catch (error) {
      button.dataset.removing = 'false';
      row?.classList.remove('cart-item--updating');
      console.error(error);
    }
  };

  const syncFromShopify = async (event) => {
    const eventSections = event?.detail?.cart?.sections;
    const hasCartPage = Boolean(document.querySelector('#main-cart-items, #main-cart-footer'));
    if (!hasCartPage && eventSections?.['cart-drawer']) {
      renderSyncedSections(eventSections);
      return;
    }

    if (syncRequest) {
      syncQueued = true;
      return syncRequest;
    }

    const cartUrl = window.routes?.cart_url || '/cart';
    const separator = cartUrl.includes('?') ? '&' : '?';
    const sectionsToSync = getSectionsToSync();

    syncRequest = fetch(`${cartUrl}${separator}sections=${encodeURIComponent(sectionsToSync.join(','))}`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart sync request failed: ${response.status}`);
        return response.json();
      })
      .then((sections) => {
        renderSyncedSections(sections);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        syncRequest = undefined;
        if (syncQueued) {
          syncQueued = false;
          syncFromShopify();
        }
      });

    return syncRequest;
  };

  document.addEventListener('change', (event) => {
    if (event.target.matches('cart-items .quantity__input')) updateFromCartInputs();
  });

  document.addEventListener(
    'click',
    (event) => {
      const removeButton = event.target.closest('cart-drawer cart-remove-button');
      if (!removeButton) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      removeDrawerItem(removeButton);
    },
    true,
  );

  document.addEventListener('cartQuantityUpdated', syncFromShopify);

  document.addEventListener('click', (event) => {
    const cartLink = event.target.closest('.header__icon--cart');
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartLink || !cartDrawer || typeof cartDrawer.open !== 'function') return;

    event.preventDefault();
    cartDrawer.open(cartLink);
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncFromShopify();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') syncFromShopify();
  });
})();
